import { isNil } from 'lodash'
import moment from 'moment'

export const formatDateTime = data => {
  const {
    applyStyling,
    isDateOnly,
    noConversion,
    returnDateOnly,
    returnFormatString,
    showTimezone,
    style,
    timezone,
    value,
    inTimezone,
    ignoreUTCTimezone,
    inUTCTimezone,
    prefix,
  } = data
  const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ss'

  const timezoneValue = inUTCTimezone ? 'Etc/UTC' : timezone || moment.tz.guess(true)

  let formatToConvert = ''

  switch (style) {
    case 1:
      formatToConvert = isDateOnly || returnDateOnly ? 'D MMMM YYYY' : 'DD/MM/YYYY h:mm A'
      break
    case 2:
      formatToConvert = isDateOnly || returnDateOnly ? 'DD/MM/YYYY' : 'MMMM Do YYYY, h:mm a'
      break
    case 3:
      formatToConvert = returnDateOnly || isDateOnly ? 'YYYY-MM-DD' : dateTimeFormat
      break
    case 4:
      formatToConvert = isDateOnly || returnDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD hh:mm:ss'
      break
    case 5:
      formatToConvert = isDateOnly || returnDateOnly ? 'D MMM YYYY' : 'YYYY-MM-DD hh:mm a'
      break
    case 6:
      formatToConvert = isDateOnly || returnDateOnly ? 'DD/MM/YYYY' : 'D MMM YYYY hh:mm a'
      break
    case 7:
      formatToConvert = isDateOnly || returnDateOnly ? 'dddd Do MMMM, YYYY' : 'h:mm a'
      break
    case 8:
      formatToConvert = isDateOnly || returnDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
      break
    case 9:
      formatToConvert = isDateOnly || returnDateOnly ? 'DD/MM/YYYY' : 'DD/MM/YYYY hh:mm:ss A'
      break
    case 10:
      formatToConvert = isDateOnly || returnDateOnly ? 'Do MMMM yyyy' : 'Do MMMM yyyy hh:mm:ss A'
      break
    default:
      return data
  }

  if (showTimezone) {
    formatToConvert = formatToConvert.concat(' (z)')
  }

  if (returnFormatString) {
    return formatToConvert
  }

  if (!value) {
    return ''
  }

  let newDateTime = !inTimezone
    ? moment.utc(value, dateTimeFormat).tz(timezoneValue).format(formatToConvert)
    : moment.tz(value, dateTimeFormat, timezoneValue).format(formatToConvert)

  if (newDateTime.match(/\(\+|-/g)) {
    formatToConvert = formatToConvert.replace('(z)', '(UTCZ)')
  }

  newDateTime = !inTimezone
    ? moment.utc(value, dateTimeFormat).tz(timezoneValue).format(formatToConvert)
    : moment.tz(value, dateTimeFormat, timezoneValue).format(formatToConvert)

  if (ignoreUTCTimezone) {
    newDateTime = moment(value).format(formatToConvert)
  }

  if (noConversion) {
    newDateTime = value
      .format(formatToConvert.replace('(z)', '').replace('(UTCZ)', ''))
      .concat(' (', getTimeZoneValue({ isDateOnly, timezone, value }), ')')
  }

  if (prefix) {
    newDateTime = prefix + newDateTime
  }

  if (applyStyling) {
    return (
      <>
        {newDateTime.split(newDateTime.match(/\(.*?\)/g))}
        <span style={{ opacity: '0.4' }}>{newDateTime.match(/\(.*?\)/g)}</span>
      </>
    )
  }

  return newDateTime
}

export const getTimeZoneValue = data => {
  const { isDateOnly, returnInFormat, timezone, value } = data
  const dateTimeFormat = isDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ss'
  let dateTimeValue = value
  const timezoneValue = timezone || moment.tz.guess()

  if (!dateTimeValue) {
    dateTimeValue = moment().tz(timezoneValue)
  }

  let timezoneCode = moment.utc(dateTimeValue, dateTimeFormat).tz(timezoneValue).format('z')

  if (timezoneCode.match(/\(\+|-/g)) {
    timezoneCode = moment.utc(dateTimeValue, dateTimeFormat).tz(timezoneValue).format('UTCZ')
  }

  if (returnInFormat) {
    // biome-ignore lint/style/useTemplate: TODO
    return ' ([' + timezoneCode + '])'
  }

  return timezoneCode
}

export const getAgeFormat = data => {
  const { timezone, value } = data
  const dateFormat = 'YYYY-MM-DD'
  const timezoneValue = timezone || moment.tz.guess()

  return (
    `${moment(
      getCurrentDateTime({
        isDateOnly: true,
        style: 3,
        timezone: timezoneValue,
      })
    ).diff(moment(value, dateFormat).format(dateFormat) || '', 'years', false)} \\y\\e\\a\\r\\s` +
    ' (' +
    moment(value, dateFormat).format('DD/MM/YYYY') +
    ')'
  )
}

export const renderDateTimeRange = data => {
  const {
    applyStyling,
    date1,
    date2,
    formatStyle,
    isDateOnly,
    inUTCTimezone,
    noConversion,
    returnAsString,
    showTimezone,
    style,
    timezone,
  } = data
  const timezoneValue = timezone || moment.tz.guess()
  let dateRange1 = ''
  let dateRange2 = ''

  switch (style) {
    case 1:
      if (date1) {
        dateRange1 = formatDateTime({
          applyStyling,
          isDateOnly,
          inUTCTimezone,
          value: date1,
          noConversion,
          style: formatStyle,
          showTimezone: isNil(showTimezone) ? true : showTimezone,
          timezone: timezoneValue,
        })
      }

      if (date2) {
        dateRange2 = formatDateTime({
          applyStyling,
          isDateOnly,
          inUTCTimezone,
          value: date2,
          noConversion,
          style: formatStyle,
          showTimezone: isNil(showTimezone) ? true : showTimezone,
          timezone: timezoneValue,
        })
      }

      if (returnAsString) {
        return dateRange1 && dateRange2 ? `${dateRange1} - ${dateRange2}` : dateRange1 || ` - ${dateRange2}`
      }

      return (
        <span>
          {dateRange1}
          {dateRange2 && ' - '}
          {dateRange2 && dateRange2}
        </span>
      )
    default:
      if (returnAsString) {
        return dateRange1 && dateRange2 ? `${dateRange1} - ${dateRange2}` : dateRange1 || ` - ${dateRange2}`
      }

      return (
        <span>
          {date1}
          {date2 && ' - '}
          {date2 && date2}
        </span>
      )
  }
}

export const getMomentDateTime = data => {
  const { isDateOnly, inUTCTimezone, timezone, value } = data
  const timezoneValue = inUTCTimezone ? 'Etc/UTC' : timezone || moment.tz.guess()

  if (!value) {
    return ''
  }

  if (moment.isMoment(value)) {
    return value
  }

  const dateTimeFormat = isDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ'

  return moment(
    moment
      .utc(value, dateTimeFormat)
      .tz(timezoneValue)
      .format(isDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ')
  )
}

export const getTimeValue = data => {
  const { timezone, value } = data
  const timezoneValue = timezone || moment.tz.guess()

  if (!value) {
    return ''
  }

  const dateTime = value.clone()

  const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ssZ'

  return moment.utc(dateTime, dateTimeFormat).tz(timezoneValue).format('hh:mm a')
}

export const getCurrentDateTime = data => {
  const { isDateOnly, inUTCTimezone, style, timezone } = data
  const timezoneValue = inUTCTimezone ? 'Etc/UTC' : timezone || moment.tz.guess()

  switch (style) {
    case 1:
      return moment.utc().tz(timezoneValue).format('DDMMYYYY')
    case 2:
      return moment.utc().tz(timezoneValue).format('DD/MM/YYYY')
    case 3:
      return moment.utc().tz(timezoneValue).format('YYYY-MM-DD')
    default:
      return moment
        .utc()
        .tz(timezoneValue)
        .format(isDateOnly ? 'DDMMYYYY' : 'YYYY-MM-DDTHH:mm:ssZ')
  }
}

export const getDateTimeForPOST = (datetime, isDateOnly, timezone, returnInMoment, returnWithoutTimezone) => {
  if (!moment.isMoment(datetime)) {
    return datetime
  }

  const timezoneValue = timezone || moment.tz.guess()
  const offset = moment(datetime).tz(timezoneValue).format('Z')
  /* eslint-disable-next-line radix */
  const newDateTime = datetime.utcOffset(parseInt(offset), true)

  if (returnInMoment) {
    return newDateTime.utc()
  }

  if (isDateOnly) {
    return newDateTime.utc().format('YYYY-MM-DD')
  }

  if (returnWithoutTimezone) {
    return newDateTime.utc().format('YYYY-MM-DD HH:mm:ss')
  }

  return newDateTime.utc().format()
}

export const replaceTimezone = data => {
  const { value, isDateOnly, initialTimezone, timezone, returnInFormat } = data
  if (!moment.isMoment(value)) {
    return value
  }

  const currentTimezone = moment.tz.guess()

  const dateTimeFormat = isDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ'

  let offset = moment(value)
    .tz(initialTimezone || currentTimezone)
    .format('Z')
  const offset2 = moment(value).tz(timezone).format('Z')

  if (!value._i.includes(offset)) {
    offset = moment(value).tz(currentTimezone).format('Z')
  }

  const newDateTime = moment(value._i.replace(offset, offset2), dateTimeFormat)

  if (returnInFormat) {
    return newDateTime._i.replace('Z', offset2)
  }

  return newDateTime
}

export const convertToUTC = data => {
  const { value, isDateOnly, returnInFormat } = data
  const dateTimeFormat = isDateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ'
  const utcValue = moment.utc(value, dateTimeFormat)

  if (returnInFormat) {
    return utcValue.format()
  }

  return utcValue
}
